'use client';

import Button from '@/components/Button';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import { useStore } from '@/providers/app-store-provider';
import { IS_DE } from '@/utils/constants';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useTranslations } from 'next-intl';

// 订单数据类型
interface OrderData {
  id: string | number;
  created_at?: string;
  order_no?: string;
  product_images?: Array<{
    image_url: string;
    is_glass: number;
    product_info?: any;
    lens_info?: any;
  }>;
  payable?: number;
  item_count?: number;
  delivery_time?: string;
  currency?: {
    symbol: string;
    full_symbol: string;
  };
  now_currency: string;
  [key: string]: any;
}

interface TrackOrderItemProps {
  order: OrderData;
  isWeb: boolean;
  onOrderClick?: (order: OrderData) => void;
}

export default function TrackOrderItem({ order, isWeb, onOrderClick }: TrackOrderItemProps) {
  const t = useTranslations('track-order.orderList');
  const currencyList = useStore((state) => state.currencyList);
  // 获取订单货币符号
  const getOrderCurrency = (orderData: OrderData) => {
    const currency = currencyList.find((item) => item.name === orderData.now_currency);
    return currency || orderData.currency;
    // return orderData.currency?.full_symbol || orderData.currency?.symbol || '$';
  };

  // 处理订单点击
  const handleOrderClick = () => {
    if (onOrderClick) {
      onOrderClick(order);
    }
  };

  // PC端布局
  const renderPCLayout = () => {
    return (
      <div className="order-item bg-white border border-[#e5e5e5] rounded-[8px]">
        <div className="order-top h-[42px] px-10 flex items-center justify-between text-gray-500  bg-[rgba(220,224,232,.26)] border-b border-[#e5e5e5]">
          <div className="flex items-center gap-[20px] font-br ">
            {order.created_at && <span className="order-time text-[14px]">{order.created_at}</span>}
            {order.order_no && (
              <span className="order-num text-[14px] font-medium">
                {t('orderNo')} {order.order_no}
              </span>
            )}
          </div>
        </div>

        <div className="order-info flex items-center gap-[20px] px-16 h-[116px]">
          <div className="flex-3 flex items-center gap-[20px] overflow-hidden">
            {/* 产品图片 */}
            {order.product_images && order.product_images.length > 0 && (
              <div className="img-box relative flex w-full gap-[10px] overflow-hidden after:absolute after:right-0 after:top-0 after:w-[42px] after:h-[100%] after:bg-[linear-gradient(90deg,hsla(0,0%,100%,0),#fff)] after:content-['']">
                {order.product_images.map((imgItem, imgIndex) => (
                  <div
                    key={imgIndex}
                    className={`img-item rounded-4 overflow-hidden w-[100px] h-[100px] min-w-[100px] min-h-[100px] flex items-center justify-center`}
                  >
                    <ProductImageWithLenses
                      imgUrl={imgItem.image_url}
                      product={imgItem.product_info}
                      lens={imgItem.lens_info}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* 价格信息 */}
            <div className="price-box flex flex-col gap-[8px] min-w-[120px] flex-1 items-end">
              {order.payable && (
                <span className="text-[16px] font-medium font-bb">
                  {t('total')}:{' '}
                  {formatPriceNumberWithSymbol(String(order.payable), getOrderCurrency(order))}
                </span>
              )}
              {order.item_count && (
                <span className="text-[14px] text-[#666]">
                  {t('qty')}: {order.item_count}
                </span>
              )}
            </div>
          </div>
          {/* 分隔线 */}
          <div className="line-col w-[1px] h-[116px] bg-[#e5e5e5] mx-[20px]" />

          {/* 交付时间 */}
          <div className="delivery-time flex-1">
            {order.delivery_time && (
              <div className="text-[14px] font-bb leading-[20px]">
                {t('estimatedDelivery')} {order.delivery_time}
              </div>
            )}
          </div>
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOrderClick}
              sx={{
                fontFamily: 'var(--font-bricolage-bold)',
                color: 'primary.main',
                borderColor: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              {t('trackOrder')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // 移动端布局
  const renderMobileLayout = () => {
    return (
      <div className="order-item bg-white border border-[#e5e5e5] rounded-[8px] p-[16px] cursor-pointer">
        <div className="order-top mb-[12px] border-b border-[#e5e5e5] pb-[12px]">
          <div className="flex justify-between items-center gap-[4px]">
            {order.order_no && (
              <span className="order-num text-[12px] font-medium text-[#282828]">
                {t('orderNo')} {order.order_no}
              </span>
            )}
            {order.created_at && (
              <span className="order-time text-[12px] text-gray-500">{order.created_at}</span>
            )}
          </div>
        </div>

        <div className="order-info">
          <div className="flex justify-between items-center gap-[12px]">
            {/* 产品图片 */}
            {order.product_images && order.product_images.length > 0 && (
              <div className="img-box w-full relative flex flex-5 gap-[8px] mb-[12px] overflow-hidden after:absolute after:right-0 after:top-0 after:w-[42px] after:h-[100%] after:bg-[linear-gradient(90deg,hsla(0,0%,100%,0),#fff)] after:content-['']">
                {order.product_images.map((imgItem, imgIndex) => (
                  <div
                    key={imgIndex}
                    className={`img-item w-[80px] h-[80px] flex items-center justify-center rounded-[6px] border border-[#e5e5e5] flex-shrink-0`}
                  >
                    <ProductImageWithLenses
                      imgUrl={imgItem.image_url}
                      product={imgItem.product_info}
                      lens={imgItem.lens_info}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* 价格和数量 */}
            <div className="price-box flex flex-2 justify-end items-center mb-[12px] text-right">
              <div className="flex flex-col gap-[4px]">
                {order.item_count && (
                  <span className="text-[12px] text-[#666]">
                    {t('qty')}: {order.item_count}
                  </span>
                )}
                {order.payable && (
                  <span className="text-[12px] font-bb text-[#333]">
                    {t('total')}:{' '}
                    {formatPriceNumberWithSymbol(String(order.payable), getOrderCurrency(order))}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 交付时间 */}
          <div className="bg-[#f5f6f7] rounded-xl p-8 flex items-center justify-between">
            {order.delivery_time ? (
              <div className="delivery-time w-3/5">
                <div className="text-[14px] text-[#666] leading-[20px]">
                  {t('estimatedDelivery')} {order.delivery_time}
                </div>
              </div>
            ) : (
              <div className="delivery-time" />
            )}

            <div>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOrderClick}
                sx={{
                  fontFamily: 'var(--font-bricolage-semibold)',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                  '&': {
                    height: '30px',
                    width: '96px',
                    padding: 0,
                  },
                }}
                style={IS_DE ? { lineHeight: 1.2, fontSize: 12 } : {}}
              >
                {t('trackOrder')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return <div>{isWeb ? renderPCLayout() : renderMobileLayout()}</div>;
}
